import { AlertTitle, DialogContentText, Stack } from "@mui/material";
import React, { Dispatch, ReactNode } from "react";

import AlertGridItem from "../../../../../Components/Helpers/AlertGridItem";
import { CustomDimension } from "../../../../../Models/CustomDimension";
import { LeadResult } from "../../../../../Models/Lead";
import LeadResultTypeLabelMask from "../../../../Constants/LeadResultTypeLabelMask";

export const classNames = {
  emailIvrWarning: "dispositionModalEmailIvrWarning"
};

export const dispositionConfirmationFormText = {
  emailIvrWarningTitle: "Please reply to the email in MAX before continuing.",
  emailIvrWarningBody: "Dispositioning the contact now will close it without the reply being sent."
} as const;

export interface IBaseDispositionConfirmationFormProps {
  /** A boolean flag which indicates if the modal containing this form has been confirmed by the user. */
  isConfirmed: boolean;
  /** The properties of the lead result which should be applied prior to persisting the confirmed lead result. */
  leadResultProps: LeadResult;
  /** The currently selected type name for the session's lead result. */
  leadResultType: CustomDimension<number>;
  /** A flag that indicates that the ivr email warning should be shown or hidden. */
  showIvrEmailWarning: boolean;
  /** A string which should be added to the end of the sentence which describes the current disposition selected. */
  summaryAddendum: string | null;
  /** Callback to modify the lead result props which are to be applied prior to persisting the confirmed lead result. */
  setLeadResultProps: Dispatch<LeadResult>;
  /** A callback which allows the form to set a reason why the modal containing this form should not be allowed to be confirmed. */
  setConfirmDisabledReason: Dispatch<string | null>;
  /** The react children of the component. */
  children?: ReactNode;
}

/** The default form which is used to confirm the selected disposition. It accepts children which allows for extension of
 * the form's contents.
 */
export const BaseDispositionConfirmationForm: React.FC<IBaseDispositionConfirmationFormProps> = ({
  leadResultType,
  summaryAddendum,
  showIvrEmailWarning,
  children
}) => {
  return (
    <Stack spacing={2}>
      {children}
      <DialogContentText>
        {`The contact disposition is "${LeadResultTypeLabelMask[leadResultType.id] || leadResultType.name}"${
          summaryAddendum || ""
        }. Please make sure this is
        accurate. You may click Cancel to go back and make corrections or Submit to move to the next contact.`}
      </DialogContentText>
      <AlertGridItem className={classNames.emailIvrWarning} severity="warning" in={showIvrEmailWarning} spacing={2}>
        <AlertTitle>{dispositionConfirmationFormText.emailIvrWarningTitle}</AlertTitle>
        {dispositionConfirmationFormText.emailIvrWarningBody}
      </AlertGridItem>
    </Stack>
  );
};

export interface IDispositionConfirmationFormProps extends IBaseDispositionConfirmationFormProps {
  /** A configuration which renders configured forms if the lead result type id matches a configured key. If the type
   * is not configured, then the base form is used.
   */
  formMap: Record<number, React.FC<IBaseDispositionConfirmationFormProps>>;
}

const DispositionConfirmationForm: React.FC<IDispositionConfirmationFormProps> = ({
  leadResultType,
  formMap,
  ...rest
}) => {
  const Form = formMap[leadResultType.id] || BaseDispositionConfirmationForm;
  return <Form leadResultType={leadResultType} {...rest} />;
};

export default DispositionConfirmationForm;
